import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const TRIAL_DAYS = 30;

export default function TrialGuard({ children }) {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Not logged in — ProtectedRoute handles redirect
  if (!user) return children;

  // Super admins and staff/managers are never trial-blocked
  // (staff accounts belong to an employer's tenant, not their own trial)
  if (user.role !== 'owner') return children;

  // Active paid subscription → always allowed
  if (user.subscription_status === 'active') return children;

  const start = new Date(user.trial_start_date || user.created_at);
  const now   = new Date();
  const daysUsed = Math.floor((now - start) / (1000 * 60 * 60 * 24));
  const daysLeft = TRIAL_DAYS - daysUsed;

  // Trial still valid
  if (daysLeft > 0) return children;

  // ── Trial expired — hard block ────────────────────────────────────────────
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-white text-center px-6 py-12"
      data-testid="trial-expired-screen"
    >
      {/* Icon */}
      <div className="w-16 h-16 rounded-full bg-orange-50 border-2 border-orange-200 flex items-center justify-center mb-6">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="w-8 h-8 text-orange-500">
          <circle cx="12" cy="12" r="10" />
          <path d="M12 6v6l3 3" />
        </svg>
      </div>

      <h1
        className="text-2xl font-bold text-slate-900 mb-3"
        style={{ fontFamily: 'Manrope, sans-serif' }}
        data-testid="trial-expired-title"
      >
        Your free trial has ended
      </h1>

      <p className="text-slate-500 text-base mb-1 max-w-sm">
        Your 30-day trial ended {Math.abs(daysLeft)} day{Math.abs(daysLeft) !== 1 ? 's' : ''} ago.
      </p>
      <p className="text-slate-500 text-base mb-8 max-w-sm">
        Upgrade your plan to continue using ReStaff.
      </p>

      <button
        onClick={() => navigate('/billing')}
        className="bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3 rounded-full shadow-md shadow-orange-200 transition-colors duration-150"
        data-testid="trial-upgrade-btn"
      >
        Upgrade Now
      </button>

      <p className="mt-4 text-xs text-slate-400">
        30-day free trial &nbsp;·&nbsp; Cancel anytime — no questions asked
      </p>
    </div>
  );
}
